
import Vue from "vue";
import moment from "moment";
import debounce from "lodash/debounce";
import { getFeedbackWithJobInfo } from "@/api";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle";
import TruncatedTextCell from "@/components/shared/TruncatedTextCell";
import AdminDataTable from "@/components/shared/AdminDataTable.vue";

type TableKey = "dailyShifts" | "changelog" | "activityLog" | "feedback";

const TABLE_KEYS: TableKey[] = [
  "dailyShifts",
  "changelog",
  "activityLog",
  "feedback"
];

const formatRecordDates = (record: any, fields: string[]) => {
  const formatted = { ...record };
  fields.forEach(field => {
    if (formatted[field]) {
      const value = moment(formatted[field]);
      formatted[field] = value.isValid()
        ? value.format("YYYY-MM-DD HH:mm:ss")
        : formatted[field];
    }
  });
  return formatted;
};

export default Vue.extend({
  name: "RawData",
  components: { TempStarsTableTitle, TruncatedTextCell, AdminDataTable },
  data() {
    const emptyCollections = TABLE_KEYS.reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {} as Record<TableKey, any[]>);
    const emptyQueries = TABLE_KEYS.reduce((acc, key) => {
      acc[key] = "";
      return acc;
    }, {} as Record<TableKey, string>);
    return {
      currentTab: 0,
      dailyShifts: [] as any[],
      changelog: [] as any[],
      activityLog: [] as any[],
      feedback: [] as any[],
      loading: {
        dailyShifts: false,
        changelog: false,
        activityLog: false,
        feedback: false
      } as Record<TableKey, boolean>,
      originalData: emptyCollections,
      searchQueries: emptyQueries,
      debouncedSearchHandlers: {} as Record<TableKey, () => void>
    };
  },
  computed: {
    dailyShiftsHeaders() {
      return [
        { text: "ID", value: "id", align: "center" },
        { text: "Job ID", value: "jobId", align: "center" },
        { text: "Shift Date", value: "shiftDate", align: "center" },
        { text: "Posted Start", value: "postedStart", align: "center" },
        { text: "Type", value: "type", align: "center" },
        { text: "Country", value: "country", align: "center" },
        { text: "First Name", value: "First Name", align: "center" },
        { text: "Last Name", value: "Last Name", align: "center" },
        { text: "Temp Phone", value: "Temp Phone", align: "center" },
        { text: "Temp Email", value: "Temp Email", align: "center" },
        { text: "Practice Name", value: "Practice Name", align: "center" },
        { text: "Practice Phone", value: "Practice Phone", align: "center" },
        { text: "Practice Email", value: "Practice Email", align: "center" },
        { text: "Shift Completed", value: "shift completed by temp", align: "center" },
        { text: "Cancelled Shifts", value: "cancelled shifts by the temp", align: "center" },
        { text: "Cancelled", value: "Cancelled", align: "center" }
      ];
    },
    changelogHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: "Changed ID", value: "changedId" },
        { text: "Admin ID", value: "adminId" },
        { text: "Table Name", value: "tablename" },
        { text: "Column Name", value: "columnname" },
        { text: "Changed From", value: "changedFrom" },
        { text: "Changed To", value: "changedTo" },
        { text: "Date", value: "date" }
      ];
    },
    activityLogHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: "Admin ID", value: "adminId" },
        { text: "Time Stamp", value: "timeStamp" },
        { text: "Button Used", value: "buttonUsed" },
        { text: "Hygienist ID", value: "hygienistId" },
        { text: "First Name", value: "firstName" },
        { text: "Last Name", value: "lastName" },
        { text: "Location ID", value: "locationId" },
        { text: "Practice Name", value: "practiceName" },
        { text: "Email", value: "email" }
      ];
    },
    feedbackHeaders() {
      return [
        { text: "Job ID", value: "jobId" },
        { text: "Hyg ID", value: "hygienistId" },
        { text: "Loc ID", value: "locationId" },
        { text: "Clinical Skills Rating", value: "clinicalSkillsRating" },
        { text: "Clinical Skills Details", value: "clinicalSkillsDetails" },
        { text: "Professionalism Rating", value: "professionalismRating" },
        { text: "Professionalism Details", value: "professionalismDetails" },
        { text: "Communication Rating", value: "communicationRating" },
        { text: "Communication Details", value: "communicationDetails" },
        { text: "Time Management Rating", value: "timeManagementRating" },
        { text: "Time Management Details", value: "timeManagementDetails" },
        { text: "Rate Satisfaction Rating", value: "rateSatisfactionRating" },
        { text: "Rate Satisfaction Details", value: "rateSatisfactionDetails" },
        { text: "Additional Feedback Details", value: "additionalFeedbackDetails" }
      ];
    }
  },
  created() {
    TABLE_KEYS.forEach(key => {
      this.debouncedSearchHandlers[key] = debounce(
        () => this.applySearch(key),
        250
      );
    });
    this.fetchDailyShifts();
  },
  watch: {
    currentTab(tab) {
      const key = TABLE_KEYS[tab];
      if (key && this[key].length === 0) {
        const method = `fetch${key.charAt(0).toUpperCase()}${key.slice(1)}`;
        this[method]();
      }
    }
  },
  methods: {
    applySearch(key: TableKey) {
      const query = this.searchQueries[key].trim().toLowerCase();
      const source = this.originalData[key] || [];
      this[key] = query
        ? source.filter(record =>
            Object.values(record).some(value =>
              value !== null && value !== undefined
                ? String(value).toLowerCase().includes(query)
                : false
            )
          )
        : source.slice();
    },
    handleSearch(key: TableKey) {
      this.debouncedSearchHandlers[key]();
    },
    formatShiftType(type: number | null) {
      if (type === 0) return "Hygienist";
      if (type === 1) return "Assistant";
      return type === null || type === undefined ? "-" : String(type);
    },
    formatCountry(country: number | null) {
      if (country === 0) return "Canada";
      if (country === 1) return "US";
      return country === null || country === undefined ? "-" : String(country);
    },
    async fetchDailyShifts() {
      this.loading.dailyShifts = true;
      try {
        await this.$store.dispatch("getData", { tablename: "DailyShiftList" });
        const records = this.$store.state.tableContent || [];
        this.originalData.dailyShifts = records.map(record =>
          formatRecordDates(record, ["shiftDate", "postedStart"])
        );
        this.applySearch("dailyShifts");
      } catch (_error) {
        this.$store.commit("callSnackbar", { text: "Error fetching daily shifts", color: "#FB7672" });
      } finally {
        this.loading.dailyShifts = false;
      }
    },
    async fetchChangelog() {
      this.loading.changelog = true;
      try {
        const records = (await this.$store.dispatch("getHistory")) || [];
        this.originalData.changelog = records.map(record =>
          formatRecordDates(record, ["date"])
        );
        this.applySearch("changelog");
      } catch (_error) {
        this.$store.commit("callSnackbar", { text: "Error fetching changelog", color: "#FB7672" });
      } finally {
        this.loading.changelog = false;
      }
    },
    async fetchActivityLog() {
      this.loading.activityLog = true;
      try {
        const records = (await this.$store.dispatch("getActivityLog")) || [];
        this.originalData.activityLog = records.map(record =>
          formatRecordDates(record, ["timeStamp"])
        );
        this.applySearch("activityLog");
      } catch (_error) {
        this.$store.commit("callSnackbar", { text: "Error fetching activity log", color: "#FB7672" });
      } finally {
        this.loading.activityLog = false;
      }
    },
    async fetchFeedback() {
      this.loading.feedback = true;
      try {
        this.originalData.feedback = await getFeedbackWithJobInfo(1, 1000);
        this.applySearch("feedback");
      } catch (_error) {
        this.$store.commit("callSnackbar", { text: "Error fetching feedback", color: "#FB7672" });
      } finally {
        this.loading.feedback = false;
      }
    }
  }
});
